import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import DocumentsList from '@/components/common/DocumentsList';
import LoadingPage from '@/components/common/LoadingPage';
import PropertiesList from '@/components/common/PropertiesList';
import SubPageHeader from '@/components/common/SubPageHeader';
import { toDateString } from '@/lib/utils/date';
import formatCurrency from '@/lib/utils/formatCurrency';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import useInvoice from './queries/hooks/useInvoice';

export default function InvoicesDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const party = useSelectedParty();

  const { data: invoice, isLoading } = useInvoice(party?.id ?? '', id ?? '', {
    enabled: Boolean(id) && Boolean(party?.id),
  });

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>
        {t('INVOICE_DETAIL_TITLE')} {invoice?.description}
      </SubPageHeader>

      <main className="flex-1 overflow-y-auto">
        {isLoading && <LoadingPage />}

        {!isLoading && invoice && (
          <div className="mt-1 flex flex-col gap-3 p-2">
            <PropertiesList
              items={[
                {
                  label: t('INVOICES_NUMBER'),
                  value: invoice.anva_invoice_nr ?? invoice.id,
                  copy: true,
                },
                {
                  label: t('INVOICES_TYPE'),
                  value: invoice.type,
                },
                {
                  label: t('INVOICES_INVOICE_DATE'),
                  value: toDateString(invoice.invoice_date),
                },
                {
                  label: t('INVOICES_DUE_DATE'),
                  value: toDateString(invoice.due_date),
                },
                {
                  label: t('INVOICES_TOTAL'),
                  value:
                    invoice.total_amount &&
                    formatCurrency(invoice.total_amount),
                },
                {
                  label: t('INVOICES_PAID'),
                  value:
                    invoice.paid_amount && formatCurrency(invoice.paid_amount),
                },
                {
                  label: t('INVOICES_OUTSTANDING'),
                  value:
                    invoice.outstanding_amount &&
                    formatCurrency(invoice.outstanding_amount),
                },
                {
                  label: t('INVOICES_POLICY'),
                  value: invoice.anva_policy_nr,
                  copy: true,
                },
              ]}
            />

            <DocumentsList documents={invoice.documents} />
          </div>
        )}
      </main>
    </div>
  );
}
